import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useSearchParams } from "react-router-dom";
import { Popconfirm, message, notification, Input, Select, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import {
  AlertMessage,
  AddLanguageModal,
  EpisodeDrawer,
  type EpisodeDrawerHandle,
  HomeSeriesPagination,
} from "../../components/common";
import { useAlert } from "../../hooks/useAlert";
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import { useSeriesButtonPermissions } from "../../hooks/useSeriesButtonPermissions";
import { getPureVideoListApi } from "../../service/task";
import {
  addFastTranslateSeriesTargetLangsApi,
  deleteFastTranslateSeriesApi,
  getFastTranslateSeriesItemListApi,
  getFastTranslateSeriesItemTargetLangPageApi,
} from "../../service/fastTranslateSeries";
import { mapFastTranslateSeriesItemListToTaskItems } from "../../utils/fastTranslateVideoListAdapter";
import { getVideoPlayUrl } from "../../utils/videoUrl";
import { downloadManyVideosMemorySafe } from "../../utils/batchVideoDownload";
import {
  createDownloadProgressNotificationKey,
  updateDownloadProgressNotification,
} from "../../utils/downloadProgressNotification";
import { getLanguageName, getLanguageNativeDisplay } from "../../utils/languageUtils";
import {
  INNER_SERIES_LIST_PAGE_PARAM,
  OUTER_SERIES_LIST_PAGE_PARAM,
  PREMIUM_VIDEO_LIST_REFACTOR_BASE,
} from "../../utils/menuPathNormalize";
import { normalizeCompletionPercent } from "../../utils/completionPercent";
import {
  LucideDownloadIcon,
  LucideTrash2Icon,
} from "../premiumSeries/premiumSeriesLucideIcons";
import { PageBackLink } from "../home/PageBack";
import { useSeriesBackTarget } from "../home/useSeriesBackTarget";
import { useSeriesContextMeta } from "../home/useSeriesContextMeta";
import "../premiumSeries/premiumSeriesInnerPage.scss";

interface QuickSeriesTargetLangGroupItem {
  seriesId: number;
  seriesName: string;
  sourceLangCode: string;
  sourceLangName: string;
  targetLangCode: string;
  targetLangName: string;
  videoCount: number;
  itemCount: number;
  needRemovalSubtitle: boolean;
  subtitleExtractType: FastTranslateSeries.SubtitleExtractType;
  completionPercent?: number | null;
}

/** 一键速译内层卡片主标题：优先接口 targetLangName，否则回退静态映射 */
function quickSeriesTargetLangZhTitle(item: QuickSeriesTargetLangGroupItem): string {
  const t = item.targetLangName?.trim();
  if (t) return t;
  return getLanguageName(item.targetLangCode || "");
}

function buildQuickVideoListTo(
  item: QuickSeriesTargetLangGroupItem,
  seriesNameFallback: string,
  innerListPage: number,
  outerListPage?: number
): string {
  const q = new URLSearchParams();
  q.set("seriesId", String(item.seriesId));
  q.set("seriesName", item.seriesName || seriesNameFallback || "");
  q.set("langCode", item.targetLangCode || "");
  if (item.targetLangName?.trim()) {
    q.set("targetLangName", item.targetLangName.trim());
  }
  q.set("from", "quick");
  if (innerListPage > 1) {
    q.set(INNER_SERIES_LIST_PAGE_PARAM, String(innerListPage));
  }
  if (outerListPage != null && outerListPage > 1) {
    q.set(OUTER_SERIES_LIST_PAGE_PARAM, String(outerListPage));
  }
  return `${PREMIUM_VIDEO_LIST_REFACTOR_BASE}?${q.toString()}`;
}

function parseInnerListPageQuery(pageVal: string | null): number {
  const n = parseInt(pageVal || "1", 10);
  return Math.max(1, Number.isFinite(n) ? n : 1);
}

export const QuickSeriesListIn: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const seriesIdStr = searchParams.get("seriesId") ?? "";
  const seriesNameParam = searchParams.get("seriesName") ?? "";
  const highlightLangParam = (searchParams.get("highlightLang") ?? "").trim();

  /** 页面级返回与剧集信息 meta pill（原 Header leading/meta，Header 移除后由页面渲染） */
  const backTarget = useSeriesBackTarget();
  const seriesMetaPill = useSeriesContextMeta(false);

  const { alertConfig, hideAlert, showError, showSuccess } = useAlert();
  const [downloadNotifyApi, downloadNotifyContextHolder] = notification.useNotification();
  const { checkSeriesButtonPermission, hasSeriesDeletePermission } = useSeriesButtonPermissions();
  const hasSeriesDownloadPermission = useCallback(
    () => checkSeriesButtonPermission("series:download"),
    [checkSeriesButtonPermission]
  );

  const seriesIdNum = useMemo(() => {
    const n = Number(seriesIdStr);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [seriesIdStr]);

  const decodedSeriesName = useMemo(() => {
    if (!seriesNameParam) return "";
    try {
      return decodeURIComponent(seriesNameParam);
    } catch {
      return seriesNameParam;
    }
  }, [seriesNameParam]);

  const episodeDrawerRef = useRef<EpisodeDrawerHandle | null>(null);
  const highlightScrollKeyDoneRef = useRef<string>("");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortValue, setSortValue] = useState<"time" | "alphabet">("alphabet");
  const [addLanguageModalVisible, setAddLanguageModalVisible] = useState(false);
  const [page, setPage] = useState(() => parseInnerListPageQuery(searchParams.get("page")));
  const PAGE_SIZE = 12;

  const [listItems, setListItems] = useState<QuickSeriesTargetLangGroupItem[]>([]);
  const [listTotal, setListTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [listRefreshTick, setListRefreshTick] = useState(0);

  const sortNum = useMemo(() => (sortValue === "time" ? 1 : 2), [sortValue]);

  const outerListPagePreserve = useMemo(() => {
    const raw = searchParams.get(OUTER_SERIES_LIST_PAGE_PARAM);
    const n = parseInt(raw || "", 10);
    return Number.isFinite(n) && n > 1 ? n : undefined;
  }, [searchParams]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const innerListPageQs = searchParams.get("page");
  useEffect(() => {
    const n = parseInnerListPageQuery(innerListPageQs);
    setPage((prev) => (prev === n ? prev : n));
  }, [innerListPageQs]);

  const pageInUrl = searchParams.get("page");
  useEffect(() => {
    if (page <= 1) {
      if (!pageInUrl) return;
    } else if (pageInUrl === String(page)) {
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (page <= 1) next.delete("page");
        else next.set("page", String(page));
        return next;
      },
      { replace: true }
    );
  }, [page, pageInUrl, setSearchParams]);

  const prevSearchSortKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${debouncedSearch}\0${sortValue}`;
    if (prevSearchSortKeyRef.current === null) {
      prevSearchSortKeyRef.current = key;
      return;
    }
    if (prevSearchSortKeyRef.current !== key) {
      prevSearchSortKeyRef.current = key;
      setPage(1);
    }
  }, [debouncedSearch, sortValue]);

  const prevSeriesIdForPagerRef = useRef<number | null>(null);
  useEffect(() => {
    if (prevSeriesIdForPagerRef.current !== null && prevSeriesIdForPagerRef.current !== seriesIdNum) {
      setPage(1);
    }
    prevSeriesIdForPagerRef.current = seriesIdNum;
  }, [seriesIdNum]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!seriesIdNum) {
        setListItems([]);
        setListTotal(0);
        return;
      }
      setListLoading(true);
      try {
        const res = await getFastTranslateSeriesItemTargetLangPageApi({
          pageNo: page,
          pageSize: PAGE_SIZE,
          seriesId: seriesIdNum,
          name: debouncedSearch || undefined,
          sort: sortNum,
        });
        if (cancelled) return;
        const data = res?.data;
        setListTotal(Number(data?.total ?? 0));
        setListItems(
          (data?.list ?? []).map((row) => ({
            seriesId: row.seriesId,
            seriesName: row.seriesName,
            sourceLangCode: row.sourceLangCode,
            sourceLangName: row.sourceLangName,
            targetLangCode: row.targetLangCode,
            targetLangName:
              (row.targetLangName && row.targetLangName.trim()) ||
              (row.target_lang_name && String(row.target_lang_name).trim()) ||
              "",
            videoCount: row.videoCount,
            itemCount: row.itemCount,
            needRemovalSubtitle: row.needRemovalSubtitle,
            subtitleExtractType: row.subtitleExtractType,
            completionPercent: row.completionPercent,
          }))
        );
      } catch {
        if (!cancelled) {
          setListItems([]);
          setListTotal(0);
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [PAGE_SIZE, page, debouncedSearch, seriesIdNum, sortNum, listRefreshTick]);

  useEffect(() => {
    highlightScrollKeyDoneRef.current = "";
  }, [seriesIdStr]);

  useEffect(() => {
    if (!highlightLangParam || listLoading) return;
    const key = `${seriesIdStr}|${highlightLangParam}`;
    if (highlightScrollKeyDoneRef.current === key) return;

    const stripHighlight = () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("highlightLang");
          return next;
        },
        { replace: true }
      );
    };

    const normalized = highlightLangParam.toLowerCase();
    const hit = listItems.find((it) => String(it.targetLangCode || "").toLowerCase() === normalized);
    if (!hit) {
      highlightScrollKeyDoneRef.current = key;
      stripHighlight();
      return;
    }

    const tryScroll = () => {
      const el = document.querySelector(`[data-series-inner-lang="${hit.targetLangCode}"]`);
      if (el instanceof HTMLElement) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    };

    highlightScrollKeyDoneRef.current = key;
    tryScroll();
    window.requestAnimationFrame(tryScroll);
    stripHighlight();
  }, [highlightLangParam, listLoading, listItems, seriesIdStr, setSearchParams]);

  const handleOpenAddLanguage = () => {
    if (!seriesIdNum) {
      showError("缺少剧集ID，无法新增语种");
      return;
    }
    setAddLanguageModalVisible(true);
  };

  const getTaskIdsForAddLanguage = async (): Promise<number[]> => {
    if (!seriesIdNum) return [];
    const response = await getPureVideoListApi(seriesIdNum, sortNum, debouncedSearch || undefined);
    if (response.code !== 0 || !response.data) return [];
    return response.data
      .filter((item: { status: number }) => item.status === 2)
      .map((item: { id: number }) => item.id);
  };

  const displaySeriesName = useMemo(() => {
    const first = listItems[0]?.seriesName;
    return first || decodedSeriesName || "剧集名称";
  }, [listItems, decodedSeriesName]);

  const drawerEpisodeInfo = useMemo(
    () => ({
      name: displaySeriesName,
      sourceLanguage: listItems[0]?.sourceLangCode || "zh",
      targetLanguage: listItems[0]?.targetLangCode || "",
      needRemovalSubtitle: !!listItems[0]?.needRemovalSubtitle,
    }),
    [displaySeriesName, listItems]
  );

  const pages = useMemo(() => Math.max(1, Math.ceil(listTotal / PAGE_SIZE) || 1), [PAGE_SIZE, listTotal]);
  const hasPrev = page > 1;
  const hasNext = page < pages;
  const hasItems = listItems.length > 0;

  const disabledLanguageCodesForAdd = useMemo(() => {
    const codeSet = new Set<string>();
    listItems.forEach((item) => {
      if (item.sourceLangCode) codeSet.add(item.sourceLangCode);
      if (item.targetLangCode) codeSet.add(item.targetLangCode);
    });
    return Array.from(codeSet);
  }, [listItems]);

  const handleLangGroupBatchDownload = useCallback(
    async (item: QuickSeriesTargetLangGroupItem) => {
      const notificationKey = createDownloadProgressNotificationKey();
      const updateProgress = (progress: number, text: string) => {
        updateDownloadProgressNotification(downloadNotifyApi, notificationKey, progress, text);
      };

      try {
        updateProgress(0, "正在获取AI速译视频列表...");

        const response = await getFastTranslateSeriesItemListApi({
          seriesId: item.seriesId,
          targetLangCode: item.targetLangCode || undefined,
          sort: sortNum,
        });

        if (response.code !== 0 || !Array.isArray(response.data)) {
          downloadNotifyApi.destroy(notificationKey);
          message.error(response.msg || "获取AI速译视频列表失败");
          return;
        }

        const videos = mapFastTranslateSeriesItemListToTaskItems(response.data);
        const downloadableVideos = videos.filter((v) => v.status === 2 && getVideoPlayUrl(v));

        if (downloadableVideos.length === 0) {
          downloadNotifyApi.destroy(notificationKey);
          message.error("没有可下载的视频，请确保视频已完成处理");
          return;
        }

        const finalSeriesName = item.seriesName || decodedSeriesName || "未命名剧集";
        const rawLangCode = String(item.targetLangCode || "").toLowerCase().trim();
        const languageName = quickSeriesTargetLangZhTitle(item) || rawLangCode;
        const safeFolderName = `${finalSeriesName}_${languageName}`.replace(
          /[<>:"/\\|?*]/g,
          "_"
        );

        const items = downloadableVideos
          .map((v) => {
            const videoUrl = getVideoPlayUrl(v);
            if (!videoUrl) return null;
            return { url: videoUrl, baseName: v.name };
          })
          .filter((x): x is { url: string; baseName: string } => x !== null);

        if (items.length === 0) {
          downloadNotifyApi.destroy(notificationKey);
          message.error("没有有效的视频下载地址");
          return;
        }

        updateProgress(5, `找到 ${items.length} 个可下载视频，准备开始下载...`);
        await new Promise((resolve) => setTimeout(resolve, 300));

        const result = await downloadManyVideosMemorySafe({
          items,
          zipDownloadFileName: `${safeFolderName}.zip`,
          folderName: safeFolderName,
          onProgress: (p) => {
            const unitProgress =
              p.fileProgress != null ? p.current + p.fileProgress : p.current;
            const pct =
              p.phase === "done"
                ? 100
                : 5 + Math.round((unitProgress / Math.max(p.total, 1)) * 90);
            updateProgress(pct, p.message);
          },
        });

        if (result.aborted) {
          downloadNotifyApi.destroy(notificationKey);
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 300));
        downloadNotifyApi.destroy(notificationKey);

        if (result.unsupportedDirectoryPicker) {
          message.error("当前浏览器不支持选择文件夹保存，请使用 Chrome 或 Edge");
          return;
        }

        if (result.success === 0) {
          message.error("所有视频保存失败，请检查网络连接或视频链接");
          return;
        }

        if (result.success < items.length) {
          message.info(
            `保存完成：成功 ${result.success} 集，失败 ${result.fail} 集（已保存到所选文件夹）`
          );
        } else {
          message.success(`已成功保存 ${result.success} 集视频到所选文件夹`);
        }
      } catch (error: unknown) {
        console.error("批量下载失败:", error);
        downloadNotifyApi.destroy(notificationKey);
        const err = error as { message?: string };
        message.error(err?.message || "批量下载失败，请稍后重试");
      }
    },
    [downloadNotifyApi, decodedSeriesName, sortNum]
  );

  const handleDeleteSeries = async (
    targetSeriesId: number,
    targetSeriesName: string,
    targetLangCode?: string
  ) => {
    try {
      const res = await deleteFastTranslateSeriesApi({
        id: targetSeriesId,
        targetLangCode: targetLangCode || undefined,
      });
      if (res?.code === 0 && res?.data) {
        showSuccess(`剧集「${targetSeriesName || "未命名"}」删除成功`);
        setListRefreshTick((t) => t + 1);
        return;
      }
      showError(res?.msg || "删除失败");
    } catch (e: unknown) {
      const err = e as { msg?: string; message?: string };
      showError(err?.msg || err?.message || "删除失败");
    }
  };

  if (!seriesIdNum) {
    return (
      <div className="premium-series-inner">
        <div className="premium-series-inner__empty">缺少有效的剧集 ID，请从剧集列表进入。</div>
      </div>
    );
  }

  return (
    <div className="premium-series-inner">
      {downloadNotifyContextHolder}
      <AlertMessage config={alertConfig} onClose={hideAlert} zIndex={ALERT_Z_INDEX.MY_WORK} />

      <div className="premium-series-inner__section-head">
        <div className="premium-series-inner__section-title-wrap">
          {backTarget ? (
            <PageBackLink to={backTarget} aria-label="返回剧集列表" />
          ) : null}
          <h3 className="premium-series-inner__section-title">多语种版本</h3>
          <span className="home-series__meta home-series__meta--pill">{seriesMetaPill}</span>
        </div>
        <div className="premium-series-inner__filters">
          <div className="premium-series-inner__search">
            <Input
              allowClear
              size="middle"
              placeholder="搜索语种..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            />
          </div>
          <Select
            className="premium-series-inner__select"
            size="middle"
            value={sortValue}
            onChange={(v) => setSortValue(v as "time" | "alphabet")}
            options={[
              { value: "time", label: "按时间排序" },
              { value: "alphabet", label: "按名称排序" },
            ]}
            getPopupContainer={(n) => n.parentElement ?? document.body}
          />
          <button type="button" className="home-series__btn-primary" onClick={handleOpenAddLanguage}>
            新增语种
          </button>
          <div className="premium-series-inner__view-toggle">
            <button
              type="button"
              className="premium-series-inner__view-btn premium-series-inner__view-btn--single"
              onClick={() => episodeDrawerRef.current?.toggleDrawer()}
              aria-label="打开剧集信息"
            >
              <i
                className="iconfont icon-shouqicebianlan premium-series-inner__view-drawer-icon"
                aria-hidden
              />
            </button>
          </div>
        </div>
      </div>

      {listLoading ? (
        <div className="premium-series-inner__loading">
          <Spin size="large" />
        </div>
      ) : !hasItems ? (
        <div className="premium-series-inner__empty">暂无多语种版本</div>
      ) : (
        <div className="premium-series-inner__grid">
          {listItems.map((item) => {
            const code = item.targetLangCode || "";
            const zhName = quickSeriesTargetLangZhTitle(item);
            const native = getLanguageNativeDisplay(code);
            const initials = zhName.charAt(0) || code.charAt(0).toUpperCase();
            const displayPct = normalizeCompletionPercent(item.completionPercent) ?? 0;
            return (
              <div
                key={`${item.seriesId}-${item.targetLangCode}`}
                className="premium-series-inner__card"
                data-series-inner-lang={code}
              >
                <div className="premium-series-inner__card-glow" />
                <NavLink
                  to={buildQuickVideoListTo(item, decodedSeriesName, page, outerListPagePreserve)}
                  className="premium-series-inner__card-main"
                >
                  <div className="premium-series-inner__card-head">
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                      <div className="premium-series-inner__lang-badge">{initials}</div>
                      <div>
                        <p className="premium-series-inner__lang-name">{zhName}</p>
                        <p className="premium-series-inner__lang-native">{native}</p>
                      </div>
                    </div>
                  </div>
                  <div className="premium-series-inner__card-body">
                    <div className="premium-series-inner__progress-label">
                      <span>译制进度</span>
                      <span style={{ fontWeight: 500 }}>{displayPct}%</span>
                    </div>
                    <div className="premium-series-inner__progress-track">
                      <div
                        className={`premium-series-inner__progress-bar${
                          displayPct === 0 ? " premium-series-inner__progress-bar--empty" : ""
                        }`}
                        style={{ width: `${displayPct}%` }}
                      />
                    </div>
                  </div>
                </NavLink>
                <div className="premium-series-inner__card-footer">
                  <div className="premium-series-inner__card-actions" style={{ marginLeft: "auto" }}>
                    {hasSeriesDownloadPermission() ? (
                      <button
                        type="button"
                        className="premium-series-inner__icon-btn"
                        title="下载"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          void handleLangGroupBatchDownload(item);
                        }}
                      >
                        <LucideDownloadIcon size={16} className="premium-series-inner__lucide" />
                      </button>
                    ) : null}
                    {hasSeriesDeletePermission() ? (
                      <Popconfirm
                        title="确定要删除这个语种分组吗？"
                        okText="确定"
                        cancelText="取消"
                        onPopupClick={(e) => {
                          e.stopPropagation();
                        }}
                        onConfirm={() => {
                          void handleDeleteSeries(
                            item.seriesId,
                            item.seriesName || decodedSeriesName || "",
                            item.targetLangCode
                          );
                        }}
                      >
                        <button
                          type="button"
                          className="premium-series-inner__icon-btn"
                          title="删除"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <LucideTrash2Icon size={16} className="premium-series-inner__lucide" />
                        </button>
                      </Popconfirm>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {listTotal > 0 && !listLoading ? (
        <div className="premium-series-inner__pager">
          <HomeSeriesPagination
            page={page}
            pageCount={pages}
            hasPrev={hasPrev}
            hasNext={hasNext}
            onPagePrev={() => setPage((p) => Math.max(1, p - 1))}
            onPageNext={() => setPage((p) => Math.min(pages, p + 1))}
          />
        </div>
      ) : null}

      <EpisodeDrawer
        ref={episodeDrawerRef}
        showToggleButton={false}
        episodeInfo={drawerEpisodeInfo}
        speakersData={[]}
        speakersLoading={false}
        seriesName={drawerEpisodeInfo.name}
        characterProfiles={[]}
      />

      <AddLanguageModal
        open={addLanguageModalVisible}
        onCancel={() => setAddLanguageModalVisible(false)}
        seriesId={String(seriesIdNum)}
        targetLanguageDictType="erp_language"
        omitTerminologyAndAiVoice
        sourceLangCode={drawerEpisodeInfo.sourceLanguage || undefined}
        getTaskIds={getTaskIdsForAddLanguage}
        disabledLanguageCodes={disabledLanguageCodesForAdd}
        onSubmitAddLanguages={({ seriesId: targetSeriesId, selectedNewLangCodes }) =>
          addFastTranslateSeriesTargetLangsApi({
            seriesId: targetSeriesId,
            targetLangCodes: JSON.stringify(selectedNewLangCodes),
          })
        }
        defaultAiVoice={true}
        emptyTaskIdsMessage="该剧集下暂无已完成的视频，请先完成视频后再新增语种"
        onSuccess={() => {
          setAddLanguageModalVisible(false);
          setListRefreshTick((t) => t + 1);
        }}
      />
    </div>
  );
};
